// Heuristic role detection for the extracted palette.
//
// Today the worker only ships back a frequency-sorted palette — it
// doesn't tag which property (color / background / link) each value came
// from. To get Brand / Text / Headings / Links / Background slots we
// apply colour-theory heuristics: lightest popular colour ≈ background,
// darkest popular colour ≈ text, most-saturated popular colour ≈ brand.
// These are best-effort guesses; the owner can override inside
// Breakdance.

import type { StyleColor } from "@clarity/shared";

export type RoleColor = { value: string; count: number };

export type RoleColors = {
  brand: RoleColor | null;
  text: RoleColor | null;
  headings: RoleColor | null;
  links: RoleColor | null;
  background: RoleColor | null;
};

export function deriveRoleColors(palette: StyleColor[]): RoleColors {
  const candidates = palette.filter((c) => /^#[0-9a-f]{6,8}$/i.test(c.value));
  if (candidates.length === 0) {
    return { brand: null, text: null, headings: null, links: null, background: null };
  }

  // Background: the lightest popular colour. Filter to those with
  // luminance > 0.85 (near white) and pick highest-count.
  const background =
    pickBest(candidates, (c) => luminance(c.value) > 0.85) ??
    pickBy(candidates, (c) => luminance(c.value), "max");

  // Text: the darkest popular colour. Filter to those with luminance
  // < 0.25 (near black) and pick highest-count.
  const text =
    pickBest(candidates, (c) => luminance(c.value) < 0.25) ??
    pickBy(candidates, (c) => luminance(c.value), "min");

  // Brand: the most-used colour that isn't greyscale and isn't text or
  // background. Saturation > 0.3 is the rough cutoff.
  const usedHexes = new Set(
    [background?.value, text?.value].filter((v): v is string => !!v),
  );
  const brand = pickBest(
    candidates.filter((c) => !usedHexes.has(c.value)),
    (c) => saturation(c.value) > 0.3,
  );

  // Headings often share text colour. Default to text and let the owner
  // tweak in Breakdance if they want a separate hue.
  const headings = text ? { value: text.value, count: text.count } : null;

  // Links: pick the most-used blue-ish colour separate from brand/text.
  const linkUsed = new Set(
    [background?.value, text?.value, brand?.value].filter((v): v is string => !!v),
  );
  const links =
    pickBest(
      candidates.filter((c) => !linkUsed.has(c.value)),
      (c) => isBlueIsh(c.value) && saturation(c.value) > 0.3,
    ) ?? null;

  return {
    brand: brand ?? null,
    text: text ?? null,
    headings,
    links,
    background: background ?? null,
  };
}

// --- helpers ---

function pickBest(
  list: StyleColor[],
  predicate: (c: StyleColor) => boolean,
): RoleColor | null {
  const matching = list.filter(predicate);
  if (matching.length === 0) return null;
  // list is already frequency-sorted by the worker, so the first match
  // wins.
  const first = matching[0];
  return first ? { value: first.value, count: first.count } : null;
}

function pickBy(
  list: StyleColor[],
  score: (c: StyleColor) => number,
  mode: "max" | "min",
): RoleColor | null {
  if (list.length === 0) return null;
  let best = list[0];
  if (!best) return null;
  let bestScore = score(best);
  for (let i = 1; i < list.length; i++) {
    const c = list[i];
    if (!c) continue;
    const s = score(c);
    if ((mode === "max" && s > bestScore) || (mode === "min" && s < bestScore)) {
      best = c;
      bestScore = s;
    }
  }
  return { value: best.value, count: best.count };
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i.exec(hex);
  if (!m || !m[1]) return null;
  const v = m[1];
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

// Rough perceived brightness in 0..1.
function luminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0.5;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

// HSL saturation in 0..1.
function saturation(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return 0;
  const d = max - min;
  return l > 0.5 ? d / (2 - max - min) : d / (max + min);
}

// "Blueish" cutoff: hue in roughly 180–260 degrees range.
function isBlueIsh(hex: string): boolean {
  const rgb = parseHex(hex);
  if (!rgb) return false;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return false;
  let h = 0;
  const d = max - min;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = h * 60;
  if (h < 0) h += 360;
  return h >= 180 && h <= 260;
}
